import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { useHidePaidUpgradesOnIos } from "@/hooks/useHidePaidUpgradesOnIos";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface BundleOption {
  id: "starter" | "plus";
  name: string;
  priceCents: number;
  chatCredits: number;
  photoCredits: number;
}

interface BillingState {
  subscriptionStatus: string | null;
  subscriptionActive: boolean;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  chatCredits: number;
  photoCredits: number;
  pricing: {
    subscriptionCents: number;
    bundles: BundleOption[];
  };
  productsConfigured: boolean;
}

interface DeliverySubscriptionEligibility {
  offered: boolean;
  sellerCount: number;
  minSellers: number;
  radiusMiles: number;
  priceCents: number;
  sellersNeeded: number;
  reason: "offered" | "disabled" | "no_location" | "not_enough_sellers";
  subscribed: boolean;
  productsConfigured: boolean;
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function Upgrade() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const isNative = Capacitor.isNativePlatform?.() ?? false;
  const hidePaidUpgrades = useHidePaidUpgradesOnIos();

  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const status = params.get("status");

  const { data, isLoading } = useQuery<BillingState>({
    queryKey: ["/api/billing/me"],
    queryFn: async () => {
      const res = await fetch("/api/billing/me", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  // Dark-launched delivery free-delivery plan. offered is false whenever the
  // server master switch is off, so this card stays hidden until Mark flips it.
  const { data: deliveryEligibility } = useQuery<DeliverySubscriptionEligibility>({
    queryKey: ["/api/delivery-subscription/eligibility"],
    queryFn: async () => {
      const res = await fetch("/api/delivery-subscription/eligibility", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const deliveryCheckout = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/delivery-subscription/checkout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (resp) => {
      window.location.href = resp.url;
    },
    onError: (err: any) => {
      toast({ title: "Couldn't start checkout", description: String(err?.message ?? err) });
    },
  });

  // Referral code for the "invite a local maker" recruiting action. Falls back
  // to a plain install link if the code has not loaded yet.
  const { data: referralCode } = useQuery<{ code: string; shareUrl: string }>({
    queryKey: ["/api/my/referral-code"],
    queryFn: async () => {
      const res = await fetch("/api/my/referral-code", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const recordInterest = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/delivery-subscription/interest", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "We'll let you know the moment it's ready in your area." });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't save that", description: String(err?.message ?? err) });
    },
  });

  const inviteMaker = async () => {
    const shareUrl =
      referralCode?.shareUrl ??
      (referralCode?.code
        ? `${window.location.origin}/install?ref=${referralCode.code}`
        : "https://trystoop.com/install");
    const text =
      "I'd love to order from you on Stoop, a local marketplace. Join here:";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Stoop", text, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast({ title: "Invite link copied" });
      }
    } catch {
      /* cancelled */
    }
  };

  const subCheckout = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/billing/checkout/subscription", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (resp) => {
      window.location.href = resp.url;
    },
    onError: (err: any) => {
      toast({ title: "Couldn't start checkout", description: String(err?.message ?? err) });
    },
  });

  const bundleCheckout = useMutation({
    mutationFn: async (bundleId: "starter" | "plus") => {
      const res = await fetch("/api/billing/checkout/bundle", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bundleId }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (resp) => {
      window.location.href = resp.url;
    },
    onError: (err: any) => {
      toast({ title: "Couldn't start checkout", description: String(err?.message ?? err) });
    },
  });

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Garden Premium</h1>
        <Button variant="outline" size="sm" onClick={() => setLocation("/garden")}>
          Back
        </Button>
      </div>

      {status === "success" ? (
        <Card className="border-green-500">
          <CardContent className="p-4 text-sm">
            Thanks! Your purchase is being processed. Credits and subscription state usually
            apply within a few seconds.
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your current state</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div>
            Subscription:{" "}
            {data.subscriptionActive ? (
              <Badge variant="default">Active</Badge>
            ) : (
              <Badge variant="outline">Free</Badge>
            )}
            {data.cancelAtPeriodEnd && data.currentPeriodEnd ? (
              <span className="ml-2 text-muted-foreground">
                Cancels {new Date(data.currentPeriodEnd).toLocaleDateString()}
              </span>
            ) : null}
          </div>
          <div>Chat credits: {data.chatCredits}</div>
          <div>Photo credits: {data.photoCredits}</div>
        </CardContent>
      </Card>

      {hidePaidUpgrades ? null : isNative ? (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            Subscription and credit purchases are managed on the web for now. Open this page in
            a browser at your usual sign-in URL to upgrade.
          </CardContent>
        </Card>
      ) : !data.productsConfigured ? (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            Premium products aren't set up yet. Check back soon.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Premium subscription · {dollars(data.pricing.subscriptionCents)}/month
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="list-disc pl-5 text-sm">
                <li>Unlimited follow-up questions on every plant</li>
                <li>Unlimited progress check-ins with harvest predictions</li>
                <li>Cancel anytime</li>
              </ul>
              <Button
                className="w-full"
                disabled={subCheckout.isPending || data.subscriptionActive}
                onClick={() => subCheckout.mutate()}
              >
                {data.subscriptionActive
                  ? "Already subscribed"
                  : subCheckout.isPending
                  ? "Starting…"
                  : "Subscribe"}
              </Button>
            </CardContent>
          </Card>

          {deliveryEligibility?.offered ? (
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="text-base">
                  Neighborhood free delivery · {dollars(deliveryEligibility.priceCents)}/month
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="list-disc pl-5 text-sm">
                  <li>
                    Free community delivery on every order within{" "}
                    {deliveryEligibility.radiusMiles} miles
                  </li>
                  <li>{deliveryEligibility.sellerCount} active sellers near you</li>
                  <li>Cancel anytime</li>
                </ul>
                <Button
                  className="w-full"
                  disabled={deliveryCheckout.isPending || deliveryEligibility.subscribed}
                  onClick={() => deliveryCheckout.mutate()}
                >
                  {deliveryEligibility.subscribed
                    ? "Already subscribed"
                    : deliveryCheckout.isPending
                    ? "Starting…"
                    : "Subscribe"}
                </Button>
              </CardContent>
            </Card>
          ) : deliveryEligibility?.reason === "not_enough_sellers" ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Free-delivery plans aren't in your area yet
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  There{" "}
                  {deliveryEligibility.sellerCount === 1 ? "is" : "are"}{" "}
                  {deliveryEligibility.sellerCount} local maker
                  {deliveryEligibility.sellerCount === 1 ? "" : "s"} within{" "}
                  {deliveryEligibility.radiusMiles} miles. We need{" "}
                  {deliveryEligibility.sellersNeeded} more to unlock free delivery
                  for you.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    className="flex-1"
                    disabled={recordInterest.isPending || recordInterest.isSuccess}
                    onClick={() => recordInterest.mutate()}
                  >
                    {recordInterest.isSuccess
                      ? "We'll notify you"
                      : recordInterest.isPending
                      ? "Saving…"
                      : "Notify me when it's ready"}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={inviteMaker}>
                    Know a local maker? Invite them
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          <div className="text-center text-xs uppercase tracking-wide text-muted-foreground">
            or one-time bundles
          </div>

          {data.pricing.bundles.map((bundle) => (
            <Card key={bundle.id}>
              <CardHeader>
                <CardTitle className="text-base">
                  {bundle.name} · {dollars(bundle.priceCents)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  {bundle.chatCredits} chat credits · {bundle.photoCredits} photo credits
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={bundleCheckout.isPending}
                  onClick={() => bundleCheckout.mutate(bundle.id)}
                >
                  {bundleCheckout.isPending ? "Starting…" : `Buy ${bundle.name}`}
                </Button>
              </CardContent>
            </Card>
          ))}

          <p className="text-xs text-muted-foreground">
            Payments are processed by Stripe. Bundles credit your account once payment confirms.
          </p>
        </>
      )}
    </div>
  );
}
